var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { computed, onMounted, ref, toRef, defineComponent } from 'vue';
import Tooltip from '../tooltip';
import abstractTooltipProps from '../tooltip/abstractTooltipProps';
import PropTypes from '../_util/vue-types';
import { initDefaultProps } from '../_util/props-util';
import { convertLegacyProps } from '../button/buttonTypes';
import ExclamationCircleFilled from '@nancal-icon/icons-vue/ExclamationCircleFilled';
import Button from '../button';
import { useLocaleReceiver } from '../locale-provider/LocaleReceiver';
import defaultLocale from '../locale-provider/default';
import { withInstall } from '../_util/type';
import useMergedState from '../_util/hooks/useMergedState';
import devWarning from '../vc-util/devWarning';
import KeyCode from '../_util/KeyCode';
import useConfigInject from '../_util/hooks/useConfigInject';
import classNames from '../_util/classNames';
import { getTransitionName } from '../_util/transition';
import { cloneVNodes } from '../_util/vnode';
import omit from '../_util/omit';
import { tooltipDefaultProps } from '../tooltip/Tooltip';
import ActionButton from '../_util/ActionButton';
export const popconfirmProps = () => (Object.assign(Object.assign({}, abstractTooltipProps()), { prefixCls: String, content: PropTypes.any, title: PropTypes.any, okType: {
        type: String,
        default: 'primary',
    }, disabled: { type: Boolean, default: false }, okText: PropTypes.any, cancelText: PropTypes.any, icon: PropTypes.any, okButtonProps: {
        type: Object,
        default: undefined,
    }, cancelButtonProps: {
        type: Object,
        default: undefined,
    }, showCancel: { type: Boolean, default: true }, onConfirm: Function, onCancel: Function }));
const Popconfirm = defineComponent({
    name: 'APopconfirm',
    props: initDefaultProps(popconfirmProps(), Object.assign(Object.assign({}, tooltipDefaultProps()), { trigger: 'click', transitionName: 'zoom-big', placement: 'top', mouseEnterDelay: 0.1, mouseLeaveDelay: 0.1, arrowPointAtCenter: false, autoAdjustOverflow: true, okType: 'primary', disabled: false })),
    slots: ['title', 'content', 'okText', 'icon', 'cancelText', 'cancelButton', 'okButton'],
    emits: ['update:visible', 'visibleChange'],
    setup(props, { slots, emit, expose }) {
        onMounted(() => {
            if (process.env.NODE_ENV !== 'production')
                devWarning(props.defaultVisible === undefined, 'Popconfirm', `'defaultVisible' is deprecated, please use 'v-model:visible'`);
        });
        const tooltipRef = ref();
        expose({
            getPopupDomNode: () => {
                var _a, _b;
                return (_b = (_a = tooltipRef.value) === null || _a === void 0 ? void 0 : _a.getPopupDomNode) === null || _b === void 0 ? void 0 : _b.call(_a);
            },
        });
        const [visible, setVisible] = useMergedState(false, {
            value: toRef(props, 'visible'),
            defaultValue: props.defaultVisible,
        });
        const settingVisible = (value, e) => {
            if (props.visible === undefined) {
                setVisible(value);
            }
            emit('update:visible', value);
            emit('visibleChange', value, e);
        };
        const close = (e) => {
            settingVisible(false, e);
        };
        const onConfirm = (e) => {
            var _a;
            return (_a = props.onConfirm) === null || _a === void 0 ? void 0 : _a.call(props, e);
        };
        const onCancel = (e) => {
            var _a;
            settingVisible(false, e);
            (_a = props.onCancel) === null || _a === void 0 ? void 0 : _a.call(props, e);
        };
        const onKeyDown = (e) => {
            if (e.keyCode === KeyCode.ESC && visible) {
                settingVisible(false, e);
            }
        };
        const onVisibleChange = (value) => {
            const { disabled } = props;
            if (disabled) {
                return;
            }
            settingVisible(value);
        };
        const { prefixCls: prefixClsConfirm, getPrefixCls } = useConfigInject('popconfirm', props);
        const rootPrefixCls = computed(() => getPrefixCls());
        const popoverPrefixCls = computed(() => getPrefixCls('popover'));
        const btnPrefixCls = computed(() => getPrefixCls('btn'));
        const [popconfirmLocale] = useLocaleReceiver('Popconfirm', defaultLocale.Popconfirm);
        const renderOverlay = () => {
            var _a, _b, _c, _d;
            const { okButtonProps, cancelButtonProps, title = (_a = slots.title) === null || _a === void 0 ? void 0 : _a.call(slots), cancelText = (_b = slots.cancel) === null || _b === void 0 ? void 0 : _b.call(slots), okText = (_c = slots.okText) === null || _c === void 0 ? void 0 : _c.call(slots), okType, icon = (_d = slots.icon) === null || _d === void 0 ? void 0 : _d.call(slots), showCancel = true, } = props;
            const { cancelButton, okButton } = slots;
            const cancelProps = Object.assign({ onClick: onCancel, size: 'small' }, cancelButtonProps);
            const okProps = Object.assign(Object.assign(Object.assign({ onClick: onConfirm }, convertLegacyProps(okType)), { size: 'small' }), okButtonProps);
            return (<div class={`${popoverPrefixCls.value}-inner-content`}>
          <div class={`${popoverPrefixCls.value}-message`}>
            {icon || <ExclamationCircleFilled />}
            <div class={`${popoverPrefixCls.value}-message-title`}>{title}</div>
          </div>
          <div class={`${popoverPrefixCls.value}-buttons`}>
            {showCancel ? (cancelButton ? (cancelButton(cancelProps)) : (<Button {...cancelProps}>{cancelText || popconfirmLocale.value.cancelText}</Button>)) : null}
            {okButton ? (okButton(okProps)) : (<ActionButton buttonProps={Object.assign(Object.assign({ size: 'small' }, convertLegacyProps(okType)), okButtonProps)} actionFn={onConfirm} close={close} prefixCls={btnPrefixCls.value} quitOnNullishReturnValue emitEvent>
                {okText || popconfirmLocale.value.okText}
              </ActionButton>)}
          </div>
        </div>);
        };
        return () => {
            var _a;
            const { placement, overlayClassName } = props, restProps = __rest(props, ["placement", "overlayClassName"]);
            const otherProps = omit(restProps, [
                'title',
                'content',
                'cancelText',
                'okText',
                'onUpdate:visible',
                'onConfirm',
                'onCancel',
            ]);
            const overlayClassNames = classNames(prefixClsConfirm.value, overlayClassName);
            return (<Tooltip {...otherProps} prefixCls={popoverPrefixCls.value} placement={placement} onVisibleChange={onVisibleChange} visible={visible.value} overlayClassName={overlayClassNames} transitionName={getTransitionName(rootPrefixCls.value, 'zoom-big', props.transitionName)} v-slots={{ title: renderOverlay }} ref={tooltipRef}>
          {cloneVNodes(((_a = slots.default) === null || _a === void 0 ? void 0 : _a.call(slots)) || [], {
                    onKeydown: (e) => {
                        onKeyDown(e);
                    },
                }, false)}
        </Tooltip>);
        };
    },
});
export default withInstall(Popconfirm);
